/** Stills and video capture straight off the WebGL canvas. */

function pickMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
  ];
  for (const type of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function timestampSlug(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export class Recorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private track: CanvasCaptureMediaStreamTrack | null = null;
  private chunks: Blob[] = [];
  private manual = false;

  constructor(private readonly canvas: HTMLCanvasElement) {}

  get isRecording(): boolean {
    return this.recorder?.state === 'recording';
  }

  get isManual(): boolean {
    return this.manual;
  }

  /**
   * Starts capturing. With `manual`, no frames are emitted until `requestFrame()`
   * is called, which keeps an exported peel exactly one layer per video frame.
   */
  start(options: { fps?: number; manual?: boolean; bitsPerSecond?: number } = {}): void {
    if (this.isRecording) return;
    if (typeof MediaRecorder === 'undefined') {
      throw new Error('This browser has no MediaRecorder, so video export is unavailable.');
    }

    const fps = options.fps ?? 60;
    this.manual = options.manual ?? false;
    this.stream = this.canvas.captureStream(this.manual ? 0 : fps);
    this.track = this.stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;

    const mimeType = pickMimeType();
    this.recorder = new MediaRecorder(this.stream, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: options.bitsPerSecond ?? 24_000_000,
    });
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start();
  }

  /** Pushes exactly one frame in manual mode. */
  requestFrame(): void {
    if (this.manual && this.track && typeof this.track.requestFrame === 'function') {
      this.track.requestFrame();
    }
  }

  async stop(): Promise<Blob | null> {
    const recorder = this.recorder;
    if (!recorder || recorder.state === 'inactive') {
      this.cleanup();
      return null;
    }

    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        resolve(new Blob(this.chunks, { type: recorder.mimeType || 'video/webm' }));
      };
      recorder.stop();
    });

    this.cleanup();
    return blob;
  }

  private cleanup(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
    }
    this.recorder = null;
    this.stream = null;
    this.track = null;
    this.chunks = [];
    this.manual = false;
  }

  /** Must be called in the same task as the draw, since the drawing buffer is not preserved. */
  async savePng(filename: string): Promise<void> {
    const blob = await new Promise<Blob | null>((resolve) => this.canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Could not read the canvas back as a PNG.');
    downloadBlob(blob, filename);
  }
}
